// ./src/components/generic/TagBadge/TagBadge.tsx
import React, { useMemo, useCallback } from "react";
import { Badge, Group, ActionIcon, Loader, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getMostContrastingColor } from "@/utils/colorHelpers";
import { Tag } from "@/utils/api/TagService";

import { useTagsContext } from "@/contexts/TagsContext";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface TagBadgeProps {
  /** Tag ID - will lookup from context */
  tagId?: string;

  /** Or pass tag directly (takes precedence over tagId) */
  tag?: Tag;

  /** Size variant */
  size?: "xs" | "sm" | "md";

  /** Show remove button */
  removable?: boolean;

  /** Remove callback */
  onRemove?: (tagId: string) => void;

  /** Whether removal is in progress */
  removing?: boolean;

  /** Click handler */
  onClick?: (tagId: string) => void;

  /** Variant: 'filled' shows colored background, 'dot' shows color dot before name */
  variant?: "filled" | "dot";

  /** Whether the badge is disabled */
  disabled?: boolean;
}

// ============================================
// Size Mappings
// ============================================

const SIZE_MAP = {
  xs: {
    badge: "xs" as const,
    icon: 10,
    dotSize: 6,
    actionIcon: 14,
  },
  sm: {
    badge: "sm" as const,
    icon: 12,
    dotSize: 8,
    actionIcon: 16,
  },
  md: {
    badge: "md" as const,
    icon: 14,
    dotSize: 10,
    actionIcon: 18,
  },
};

// Default tag color when none specified
const DEFAULT_TAG_COLOR = "#6B7280"; // Gray-500

// ============================================
// Component
// ============================================

const TagBadgeComponent: React.FC<TagBadgeProps> = ({
  tagId,
  tag: tagProp,
  size = "sm",
  removable = false,
  onRemove,
  removing = false,
  onClick,
  variant = "filled",
  disabled = false,
}) => {
  const { t } = useTranslation(["common"]);
  const { getTagById } = useTagsContext();

  // Get tag from context if tagId provided, otherwise use prop
  const tag = useMemo(() => {
    if (tagProp) return tagProp;
    if (tagId) return getTagById(tagId);
    return undefined;
  }, [tagProp, tagId, getTagById]);

  // Get size configuration
  const sizeConfig = SIZE_MAP[size];

  // Derive colors
  const tagColor = tag?.color || DEFAULT_TAG_COLOR;
  const textColor = useMemo(
    () => getMostContrastingColor(tagColor),
    [tagColor]
  );

  // Handlers
  const handleClick = useCallback(() => {
    if (disabled || !onClick) return;
    const id = tag?.id || tagId;
    if (id) onClick(id);
  }, [disabled, onClick, tag?.id, tagId]);

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled || removing || !onRemove) return;
      const id = tag?.id || tagId;
      if (id) onRemove(id);
    },
    [disabled, removing, onRemove, tag?.id, tagId]
  );

  // Build right section (remove button)
  // ⚠ Above the loading badge's `return` (#129): below it, the tag arriving rendered one hook more than the loading
  // render, and React threw ("Rendered more hooks than during the previous render").
  const rightSection = useMemo(() => {
    if (!removable) return undefined;

    if (removing) {
      return <Loader size={sizeConfig.icon} color={textColor} />;
    }

    return (
      <Tooltip label={t("common:buttons.remove")} withArrow position="top">
        <ActionIcon
          size={sizeConfig.actionIcon}
          variant="transparent"
          onClick={handleRemove}
          disabled={disabled}
          aria-label={t("common:buttons.remove")}
          style={{ color: textColor }}
        >
          <AppIcon iconName="X" size={sizeConfig.icon} />
        </ActionIcon>
      </Tooltip>
    );
  }, [removable, removing, sizeConfig, textColor, handleRemove, disabled, t]);

  // Loading state - tag not found
  if (!tag) {
    return (
      <Badge
        size={sizeConfig.badge}
        variant="light"
        color="gray"
        leftSection={<Loader size={sizeConfig.icon} color="gray" />}
      >
        {t("common:loading")}
      </Badge>
    );
  }

  // Dot variant - shows color dot before name
  if (variant === "dot") {
    return (
      <Badge
        size={sizeConfig.badge}
        variant="light"
        color="gray"
        leftSection={
          <span
            style={{
              width: sizeConfig.dotSize,
              height: sizeConfig.dotSize,
              borderRadius: "50%",
              backgroundColor: tagColor,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
        }
        rightSection={rightSection}
        onClick={onClick ? handleClick : undefined}
        style={{
          cursor: onClick && !disabled ? "pointer" : undefined,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {tag.name}
      </Badge>
    );
  }

  // Filled variant - colored background with contrasting text
  return (
    <Badge
      size={sizeConfig.badge}
      variant="filled"
      rightSection={rightSection}
      onClick={onClick ? handleClick : undefined}
      style={{
        backgroundColor: tagColor,
        color: textColor,
        cursor: onClick && !disabled ? "pointer" : undefined,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {tag.name}
    </Badge>
  );
};

export const TagBadge = React.memo(TagBadgeComponent);

// ============================================
// TagBadgeGroup - Helper for rendering multiple tags
// ============================================

export interface TagBadgeGroupProps {
  /** Array of tag IDs */
  tagIds?: string[];

  /** Or array of tag objects */
  tags?: Tag[];

  /** Size for all badges */
  size?: "xs" | "sm" | "md";

  /** Variant for all badges */
  variant?: "filled" | "dot";

  /** Whether badges are removable */
  removable?: boolean;

  /** Remove callback */
  onRemove?: (tagId: string) => void;

  /** Tag being removed */
  removingTagId?: string | null;

  /** Click callback */
  onClick?: (tagId: string) => void;

  /** Max tags to show before "+N more" */
  maxVisible?: number;

  /** Gap between badges */
  gap?: "xs" | "sm" | "md" | number;

  /** Whether all badges are disabled */
  disabled?: boolean;
}

const TagBadgeGroupComponent: React.FC<TagBadgeGroupProps> = ({
  tagIds,
  tags,
  size = "sm",
  variant = "filled",
  removable = false,
  onRemove,
  removingTagId,
  onClick,
  maxVisible,
  gap = "xs",
  disabled = false,
}) => {
  const { t } = useTranslation(["common"]);

  // Determine which tags to render
  const renderItems = useMemo(() => {
    if (tags && tags.length > 0) {
      return tags.map((tag) => ({ type: "tag" as const, tag, id: tag.id }));
    }
    if (tagIds && tagIds.length > 0) {
      return tagIds.map((id) => ({ type: "id" as const, tagId: id, id }));
    }
    return [];
  }, [tags, tagIds]);

  // Apply maxVisible limit
  const visibleItems = useMemo(() => {
    if (maxVisible && renderItems.length > maxVisible) {
      return renderItems.slice(0, maxVisible);
    }
    return renderItems;
  }, [renderItems, maxVisible]);

  const hiddenCount = renderItems.length - visibleItems.length;

  if (renderItems.length === 0) {
    return null;
  }

  return (
    <Group gap={gap} wrap="wrap">
      {visibleItems.map((item) => (
        <TagBadge
          key={item.id}
          tagId={item.type === "id" ? item.tagId : undefined}
          tag={item.type === "tag" ? item.tag : undefined}
          size={size}
          variant={variant}
          removable={removable}
          onRemove={onRemove}
          removing={removingTagId === item.id}
          onClick={onClick}
          disabled={disabled}
        />
      ))}
      {hiddenCount > 0 && (
        <Badge size={SIZE_MAP[size].badge} variant="light" color="gray">
          +{hiddenCount} {t("common:more")}
        </Badge>
      )}
    </Group>
  );
};

export const TagBadgeGroup = React.memo(TagBadgeGroupComponent);
